if (!customElements.get('product-information')) {
  customElements.define('product-information', class ProductInformation extends HTMLElement {
    staticBuyTools = true;
    quantityInput = undefined;
    onVariantChangeUnsubscriber = undefined;
    cartUpdateUnsubscriber = undefined;
    abortController = undefined;
    pendingRequestUrl = null;
    preProcessHtmlCallbacks = [];
    postProcessHtmlCallbacks = [];

    constructor() {
      super();

      this.buyToolsTrigger = this.querySelector(
        '#product__buy-tools-trigger'
      );

      this.productInfoContainer = this.querySelector(
        '.product__info-container'
      );

      this.onScrollToolsHandler = this.onScrollBuyTools.bind(this);

      this.quantityInput = this.querySelector(
        `#QuantityInput-${this.sectionId}`
      );

      this.initBreakpoints();
    }

    initBreakpoints() {
      const self = this;

      enquire.register('screen and (max-width: 1000px)', {
        match: () => {
          self.bpSmall = true;

          this.productInfoContainer?.classList.remove(
            'product__info-container--modal--out',
            'product__info-container--modal',
            'background-modal'
          );
        },

        unmatch: () => {
          self.bpSmall = false;

          this.productInfoContainer?.classList.remove(
            'product__info-container--modal--out',
            'product__info-container--modal',
            'background-modal'
          );
        }
      });
    }

    connectedCallback() {
      this.initializeProductSwapUtility();

      this.onVariantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.optionValueSelectionChange,
        this.handleOptionValueChange.bind(this)
      );

      this.initializeKitCarryOver();
    }

    addPreProcessCallback(callback) {
      this.preProcessHtmlCallbacks.push(callback);
    }

    disconnectedCallback() {
      this.onVariantChangeUnsubscriber?.();
      this.kitCarryAbortController?.abort();
      this.resizeProductInfoObserver?.disconnect();
    }

    initializeProductSwapUtility() {
      this.preProcessHtmlCallbacks.push((html) => {
        html.querySelectorAll('.scroll-trigger').forEach((element) => {
          element.classList.add('scroll-trigger--cancel');
        });
      });

      this.postProcessHtmlCallbacks.push(() => {
        window?.Shopify?.PaymentButton?.init();
        window?.ProductModel?.loadShopifyXR();
      });
    }

    handleOptionValueChange({
      data: { event, target, selectedOptionValues }
    }) {
      if (!this.contains(event.target)) return;

      this.resetProductFormState();

      const productUrl =
        target.dataset.productUrl ||
        this.pendingRequestUrl ||
        this.dataset.url;

      this.pendingRequestUrl = productUrl;

      const shouldSwapProduct = this.dataset.url !== productUrl;

      const shouldFetchFullPage =
        this.dataset.updateUrl === 'true' &&
        shouldSwapProduct;

      this.renderProductInfo({
        requestUrl: this.buildRequestUrlWithParams(
          productUrl,
          selectedOptionValues,
          shouldFetchFullPage
        ),
        targetId: target.id,
        callback: shouldSwapProduct
          ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
          : this.handleUpdateProductInfo(productUrl)
      });
    }

    resetProductFormState() {
      this.toggleSubmitButton(true);
      this.productForm?.handleErrorMessage();
    }

    handleSwapProduct(productUrl, updateFullPage) {
      return (html) => {
        this.productModal?.remove();

        const selector = updateFullPage
          ? "product-information[id^='MainProduct']"
          : 'product-information';

        const variant = this.getSelectedVariant(
          html.querySelector(selector)
        );

        this.updateURL(productUrl, variant?.id);

        if (updateFullPage) {
          const incomingTitle = html.querySelector('head title');

          if (incomingTitle) {
            document.title = incomingTitle.textContent;
          }

          HTMLUpdateUtility.viewTransition(
            document.querySelector('main'),
            html.querySelector('main'),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );
        } else {
          HTMLUpdateUtility.viewTransition(
            this,
            html.querySelector('product-information'),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );
        }
      };
    }

    renderProductInfo({ requestUrl, targetId, callback }) {
      this.abortController?.abort();
      this.abortController = new AbortController();

      fetch(requestUrl, {
        signal: this.abortController.signal
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error('Could not load product information.');
          }

          return response.text();
        })
        .then((responseText) => {
          this.pendingRequestUrl = null;

          const html = new DOMParser().parseFromString(
            responseText,
            'text/html'
          );

          callback(html);
        })
        .then(() => {
          document.getElementById(targetId)?.focus();
        })
        .catch((error) => {
          if (error.name !== 'AbortError') {
            console.error(error);
          }
        });
    }

    getSelectedVariant(productInfoNode) {
      const selectedVariant = productInfoNode?.querySelector(
        'variant-selects [data-selected-variant]'
      )?.textContent;

      if (!selectedVariant?.trim()) return null;

      try {
        return JSON.parse(selectedVariant);
      } catch (error) {
        return null;
      }
    }

    getKitCarryParameters() {
      const currentUrl = new URL(window.location.href);

      const sourceHandle = currentUrl.searchParams.get('kit_from');
      const sourceVariant = currentUrl.searchParams.get(
        'kit_from_variant'
      );

      // Treat URL values as untrusted references.
      if (
        !sourceHandle ||
        sourceHandle.length > 255 ||
        !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(sourceHandle)
      ) {
        return null;
      }

      return {
        handle: sourceHandle,
        variantId:
          sourceVariant && /^\d+$/.test(sourceVariant)
            ? sourceVariant
            : null
      };
    }

    buildRequestUrlWithParams(
      url,
      optionValues,
      shouldFetchFullPage = false
    ) {
      const requestUrl = new URL(url, window.location.origin);

      if (requestUrl.origin !== window.location.origin) {
        throw new Error('Product requests must stay on this storefront.');
      }

      if (!shouldFetchFullPage) {
        requestUrl.searchParams.set('section_id', this.sectionId);
      }

      if (optionValues.length) {
        requestUrl.searchParams.set(
          'option_values',
          optionValues.join(',')
        );
      } else {
        requestUrl.searchParams.delete('option_values');
      }

      return requestUrl.pathname + requestUrl.search;
    }

    updateOptionValues(html) {
      const sectionId =
        `${this.sectionId}${this.blockId ? this.blockId : ''}`;

      const variantSelects = html.getElementById(
        `VariantSelect-${sectionId}`
      );

      if (!variantSelects) return;

      const isOnProductModal =
        this.closest('complementary-products-card') ||
        this.closest('quick-add-modal') ||
        this.closest('.shop-look__item-wrap');

      if (isOnProductModal) {
        const updatedOptions = variantSelects.querySelectorAll(
          'fieldset'
        );

        this.variantSelectors.querySelectorAll(
          'input[data-disabled="true"]'
        ).forEach((input) => {
          input.removeAttribute('data-disabled');
        });

        updatedOptions.forEach((option) => {
          option.querySelectorAll(
            'input[data-disabled="true"]'
          ).forEach((input) => {
            const inputId = input.dataset.optionValueId;

            const currentNode = this.variantSelectors.querySelector(
              `input[data-option-value-id="${inputId}"]`
            );

            if (
              currentNode?.tagName === 'INPUT' &&
              currentNode.type === 'radio'
            ) {
              currentNode.setAttribute('data-disabled', 'true');
            }
          });
        });
      } else {
        HTMLUpdateUtility.viewTransition(
          this.variantSelectors,
          variantSelects,
          this.preProcessHtmlCallbacks
        );

        // Reapply visual highlights after the picker is replaced.
        this.applyKitCarryHighlight();
      }
    }

    handleUpdateProductInfo(productUrl) {
      return (html) => {
        const variant = this.getSelectedVariant(html);

        const sectionId =
          `${this.sectionId}${this.blockId ? this.blockId : ''}`;

        this.pickupAvailability?.update(variant);
        this.updateOptionValues(html);
        this.updateURL(productUrl, variant?.id);
        this.updateVariantInputs(variant?.id);

        if (!variant) {
          this.setUnavailable();
          return;
        }

        this.updateMedia(variant, variant.featured_media?.id);

        const updateSourceFromDestination = (
          id,
          shouldHide = () => false
        ) => {
          const source = html.getElementById(`${id}-${sectionId}`);

          const destination = this.querySelector(
            `#${id}-${this.dataset.section}`
          );

          if (source && destination) {
            destination.innerHTML = source.innerHTML;

            destination.classList.toggle(
              'visibility-hidden',
              shouldHide(source)
            );
          }
        };

        updateSourceFromDestination('price');

        updateSourceFromDestination(
          'sku',
          ({ classList }) => classList.contains('visibility-hidden')
        );

        updateSourceFromDestination(
          'StockNotification',
          ({ innerText }) => innerText === ''
        );

        this.updateLimitQuantity(this.sectionId, html);

        this.toggleSubmitButton(
          html.getElementById(
            `ProductSubmitButton-${sectionId}`
          )?.hasAttribute('disabled') ?? true,
          window.variantStrings.soldOut
        );

        publish(PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId: this.sectionId,
            html,
            variant
          }
        });
      };
    }

    updateLimitQuantity(sectionId, html) {
      if (!this.quantityInput) return;

      const quantityFormUpdated = html.getElementById(
        `QuantityInput-${sectionId}`
      );

      if (quantityFormUpdated) {
        this.quantityInput.updateLimitQuantity(quantityFormUpdated);
      }
    }

    updateVariantInputs(variantId) {
      const sectionId = this.dataset.section;

      this.querySelectorAll(
        `#product-form-${sectionId}, #product-form-installment-${sectionId}`
      ).forEach((productForm) => {
        const input = productForm.querySelector('input[name="id"]');

        if (!input) return;

        input.value = variantId ?? '';

        input.dispatchEvent(
          new Event('change', { bubbles: true })
        );
      });
    }

    updateURL(url, variantId) {
      const destination = new URL(url, window.location.origin);

      if (destination.origin !== window.location.origin) return;

      // Share links remain normal product/variant links.
      const shareUrl = new URL(destination.href);

      shareUrl.searchParams.delete('kit_from');
      shareUrl.searchParams.delete('kit_from_variant');

      if (variantId) {
        shareUrl.searchParams.set('variant', variantId);
      } else {
        shareUrl.searchParams.delete('variant');
      }

      this.querySelector('share-button')?.updateUrl(shareUrl.href);

      if (this.dataset.updateUrl === 'false') return;

      const carry = this.getKitCarryParameters();
      const currentUrl = new URL(window.location.href);

      // Carry-over belongs to the current bundle page only.
      if (
        carry &&
        destination.pathname === currentUrl.pathname
      ) {
        destination.searchParams.set('kit_from', carry.handle);

        if (carry.variantId) {
          destination.searchParams.set(
            'kit_from_variant',
            carry.variantId
          );
        } else {
          destination.searchParams.delete('kit_from_variant');
        }
      } else {
        destination.searchParams.delete('kit_from');
        destination.searchParams.delete('kit_from_variant');
      }

      if (variantId) {
        destination.searchParams.set('variant', variantId);
      } else {
        destination.searchParams.delete('variant');
      }

      window.history.replaceState(
        window.history.state,
        '',
        destination.pathname + destination.search + destination.hash
      );
    }
    async initializeKitCarryOver() {
      // Carry-over is only used on the main product page.
      if (
        this.dataset.updateUrl !== 'true' ||
        !this.id.startsWith('MainProduct-')
      ) {
        return;
      }

      const carry = this.getKitCarryParameters();

      const banner = this.querySelector(
        '[data-kit-source-banner]'
      );

      if (!carry || !carry.variantId || !banner) return;

      this.kitCarryAbortController?.abort();
      this.kitCarryAbortController = new AbortController();

      try {
        const root = new URL(
          window.Shopify?.routes?.root || '/',
          window.location.origin
        );

        if (root.origin !== window.location.origin) return;

        const sourceUrl = new URL(
          `products/${encodeURIComponent(carry.handle)}.js`,
          root
        );

        const response = await fetch(sourceUrl.href, {
          headers: {
            Accept: 'application/json'
          },
          credentials: 'same-origin',
          signal: this.kitCarryAbortController.signal
        });

        if (!response.ok) return;

        const sourceProduct = await response.json();

        if (
          !this.isConnected ||
          sourceProduct.handle !== carry.handle ||
          !Array.isArray(sourceProduct.variants) ||
          String(sourceProduct.id) === String(this.productId)
        ) {
          return;
        }

        // The carried variant must actually belong to this product.
        // Never fall back to a different variant.
        const sourceVariant = sourceProduct.variants.find(
          (variant) => String(variant.id) === carry.variantId
        );

        if (!sourceVariant) return;

        this.kitCarryProduct = sourceProduct;
        this.kitCarryVariant = sourceVariant;

        banner.hidden = true;
this.applyKitCarryHighlight();

        this.kitCarryObserver?.disconnect();

        this.kitCarryObserver = new MutationObserver(() => {
          this.applyKitCarryHighlight();
        });

        this.kitCarryObserver.observe(this, {
          childList: true,
          subtree: true
        });

        // Disconnect when this product element leaves the document.
        this.kitCarryConnectionObserver?.disconnect();

        this.kitCarryConnectionObserver = new MutationObserver(() => {
          if (this.isConnected) return;

          this.kitCarryObserver?.disconnect();
          this.kitCarryConnectionObserver?.disconnect();
          this.kitCarryAbortController?.abort();
        });

        this.kitCarryConnectionObserver.observe(
          document.documentElement,
          {
            childList: true,
            subtree: true
          }
        );
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.warn('Could not load the original kit.', error);
        }
      }
    }

    normalizeKitMatch(value) {
      return String(value || '')
        .toLowerCase()
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
    }

    applyKitCarryHighlight() {
      const picker = this.variantSelectors;

      if (!picker || !this.kitCarryProduct) return;

      picker.querySelectorAll(
        '[data-kit-carry-match]'
      ).forEach((element) => {
        element.removeAttribute('data-kit-carry-match');
      });

      const sourceName = this.normalizeKitMatch(
        this.kitCarryProduct.title
      );

      const sourceTokens = new Set(sourceName.split(' '));

      // These describe the general product, rather than its fitment.
      const generalWords = new Set([
        'magnetic',
        'number',
        'plate',
        'plates',
        'kit',
        'set',
        'and',
        'for',
        'with',
        'the',
        'of',
        'front',
        'rear'
      ]);

      const groups = Array.from(
        picker.querySelectorAll(
          'fieldset[data-option-position]'
        )
      );

      // A missing/older position hook still allows normal fieldsets.
      if (!groups.length) {
        groups.push(...picker.querySelectorAll('fieldset'));
      }

      const candidates = [];

      groups.forEach((group) => {
        group.querySelectorAll(
          'input[type="radio"]'
        ).forEach((input) => {
          const optionName = this.normalizeKitMatch(input.value);

          const meaningfulTokens = optionName
            .split(' ')
            .filter((token) => token && !generalWords.has(token));

          if (!meaningfulTokens.length) return;

          // Require every meaningful word in the option to occur
          // in the source product name, not just one shared word.
          const matches = meaningfulTokens.every(
            (token) => sourceTokens.has(token)
          );

          if (!matches) return;

          candidates.push({
            input,
            score: meaningfulTokens.reduce(
              (score, token) => score + token.length,
              0
            )
          });
        });
      });

      if (!candidates.length) return;

      const bestScore = Math.max(
        ...candidates.map((candidate) => candidate.score)
      );

      const bestMatches = candidates.filter(
        (candidate) => candidate.score === bestScore
      );

      // Ambiguous names should not produce a misleading highlight.
      if (bestMatches.length !== 1) return;

      const input = bestMatches[0].input;

      // Only add a visual hook. Do not change checked, disabled,
      // data-disabled, data-combo-hidden, or the selected variant.
      input.setAttribute('data-kit-carry-match', 'true');

      const label = Array.from(
        picker.querySelectorAll('label')
      ).find((element) => element.htmlFor === input.id);

      label?.setAttribute('data-kit-carry-match', 'true');
    }

    formatKitSourcePrice(price) {
      const amount = Number(price);

      if (!Number.isFinite(amount)) return '';

      const banner = this.querySelector(
        '[data-kit-source-banner]'
      );

      const currency =
        window.Shopify?.currency?.active ||
        banner?.dataset.currency;

      if (!currency) return '';

      try {
        return new Intl.NumberFormat(
          document.documentElement.lang || 'en-GB',
          {
            style: 'currency',
            currency
          }
        ).format(amount / 100);
      } catch (error) {
        return '';
      }
    }

    renderKitSourceBanner() {
      const banner = this.querySelector(
        '[data-kit-source-banner]'
      );

      const product = this.kitCarryProduct;
      const variant = this.kitCarryVariant;

      if (!banner || !product || !variant) return;

      const title = banner.querySelector(
        '[data-kit-source-title]'
      );

      const variantTitle = banner.querySelector(
        '[data-kit-source-variant-title]'
      );

      const price = banner.querySelector(
        '[data-kit-source-price]'
      );

      const image = banner.querySelector(
        '[data-kit-source-image]'
      );

      const button = banner.querySelector(
        '[data-kit-source-continue]'
      );

      const error = banner.querySelector(
        '[data-kit-source-error]'
      );

      if (title) {
        title.textContent = product.title;
      }

      if (variantTitle) {
        const hasVariantTitle =
          variant.title &&
          variant.title !== 'Default Title';

        variantTitle.textContent = hasVariantTitle
          ? variant.title
          : '';

        variantTitle.hidden = !hasVariantTitle;
      }

      if (price) {
        price.textContent = this.formatKitSourcePrice(
          variant.price
        );
      }

      if (image) {
        image.hidden = true;
        image.removeAttribute('src');

        const imageSource =
          variant.featured_image?.src ||
          product.featured_image;

        if (typeof imageSource === 'string' && imageSource) {
          try {
            const imageUrl = new URL(
              imageSource,
              window.location.origin
            );

            const isTrustedImage =
              imageUrl.protocol === 'https:' &&
              (
                imageUrl.origin === window.location.origin ||
                imageUrl.hostname === 'cdn.shopify.com'
              );

            if (isTrustedImage) {
              image.src = imageUrl.href;
              image.alt = product.title;
              image.hidden = false;
            }
          } catch (imageError) {
            // An invalid image must not prevent the banner working.
          }
        }
      }

      if (error) {
        error.textContent = '';
        error.hidden = true;
      }

      if (button) {
        button.disabled = !variant.available;
        button.setAttribute(
          'aria-disabled',
          String(!variant.available)
        );

        if (!variant.available) {
          button.textContent =
            banner.dataset.unavailableLabel ||
            window.variantStrings.soldOut;
        }

        if (!button.dataset.kitSourceBound) {
          button.dataset.kitSourceBound = 'true';

          button.addEventListener('click', () => {
            this.addKitSourceToBasket();
          });
        }
      }

      banner.hidden = false;
    }

    async addKitSourceToBasket() {
      const banner = this.querySelector(
        '[data-kit-source-banner]'
      );

      const button = banner?.querySelector(
        '[data-kit-source-continue]'
      );

      const errorElement = banner?.querySelector(
        '[data-kit-source-error]'
      );

      const variant = this.kitCarryVariant;
      const product = this.kitCarryProduct;

      if (
        !button ||
        this.kitSourceAdding ||
        !variant?.available ||
        !product?.variants?.some(
          (item) => String(item.id) === String(variant.id)
        )
      ) {
        return;
      }

      this.kitSourceAdding = true;

      button.disabled = true;
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');

      if (errorElement) {
        errorElement.textContent = '';
        errorElement.hidden = true;
      }

      try {
        const root = new URL(
          window.Shopify?.routes?.root || '/',
          window.location.origin
        );

        if (root.origin !== window.location.origin) {
          throw new Error('Invalid cart destination.');
        }

        const response = await fetch(
          new URL('cart/add.js', root).href,
          {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify({
              items: [
                {
                  id: variant.id,
                  quantity: 1
                }
              ]
            })
          }
        );

        const result = await response.json();

        if (!response.ok) {
          throw new Error(
            typeof result.description === 'string'
              ? result.description
              : 'Could not add the original product.'
          );
        }

        // This action adds only the original variant.
        // The bundle's normal form remains entirely separate.
        window.location.assign(
          new URL('cart', root).href
        );
      } catch (error) {
        if (errorElement) {
          errorElement.textContent =
            banner.dataset.errorMessage ||
            'Sorry, we could not add your original kit. Please try again.';

          errorElement.hidden = false;
        }

        console.error('Original kit add-to-cart error:', error);

        this.kitSourceAdding = false;
        button.disabled = false;
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
      }
    }
    toggleSubmitButton(disable = true, text) {
      const productForm = this.productForm?.querySelector('form');
      if (!productForm) return;

      const addButton = productForm.querySelector(
        '[data-add-to-cart]'
      );

      const addButtonText = productForm.querySelector(
        '[data-add-to-cart-text]'
      );

      if (!addButton) return;

      const variantClassSoldOut = 'product-form--variant-sold-out';

      const formParentElement = productForm.closest(
        '.product-form__buy-buttons'
      );

      const productMetaPrice = formParentElement
        ? formParentElement.querySelector('[data-product-meta-price]')
        : null;

      if (disable) {
        addButton.setAttribute('aria-disabled', 'true');
        addButton.setAttribute('disabled', 'true');
        addButton.setAttribute('aria-label', text);

        if (text) {
          addButtonText.textContent = text;
          productForm.classList.add(variantClassSoldOut);

          if (productMetaPrice) {
            productMetaPrice.classList.add('meta-price--hidden');
          }
        }
      } else {
        addButton.removeAttribute('aria-disabled');
        addButton.removeAttribute('disabled');

        addButton.setAttribute(
          'aria-label',
          window.variantStrings.addToCart
        );

        addButtonText.textContent = window.variantStrings.addToCart;
        productForm.classList.remove(variantClassSoldOut);

        if (productMetaPrice) {
          productMetaPrice.classList.remove('meta-price--hidden');
        }
      }
    }

    setUnavailable() {
      this.toggleSubmitButton(
        true,
        window.variantStrings.unavailable
      );

      const selectors = ['price', 'sku', 'StockNotification']
        .map((id) => `#${id}-${this.dataset.section}`)
        .join(', ');

      document.querySelectorAll(selectors).forEach(
        ({ classList }) => {
          classList.add('visibility-hidden');
        }
      );
    }

    updateMedia(variant, variantFeaturedMediaId) {
      if (!variantFeaturedMediaId) return;

      const sectionId = this.dataset.section;

      const mediaGallery = this.querySelector(
        `#MediaGallery-${sectionId}`
      );

      if (!mediaGallery) return;

      mediaGallery.setActiveMedia(
        `${sectionId}-${variantFeaturedMediaId}`,
        variant
      );
    }

    buyToolsCalculations() {
      this.staticBuyTools = false;
      this.productPageSection = this.parentElement;

      this.productInfoWrapper =
        this.productPageSection.querySelector(
          '.product__info-wrapper'
        );

      this.productInfoContainerHeight =
        this.productInfoContainer.offsetHeight;

      this.negativeHeight =
        this.productInfoContainerHeight * -1;

      this.productContentSection = document.querySelector(
        '.product-content-section'
      );

      const productContentMinHeight =
        this.productInfoContainerHeight -
        this.productPageSection.offsetHeight +
        54;

      this.productContentSection.style.minHeight =
        `${productContentMinHeight}px`;

      this.resizeProductInfoObserver = new ResizeObserver(() => {
        if (this.bpSmall) return;

        if (
          !this.productInfoContainer.classList.contains(
            'product__info-container--modal'
          )
        ) {
          const productInfoContainerHeight =
            this.productInfoContainer.offsetHeight;

          if (
            productInfoContainerHeight !==
            this.productInfoContainerHeight
          ) {
            this.productInfoContainerHeight =
              productInfoContainerHeight;

            const productContentMinHeight =
              productInfoContainerHeight -
              this.productPageSection.offsetHeight +
              54;

            this.productContentSection.style.minHeight =
              `${productContentMinHeight}px`;

            this.negativeHeight =
              productInfoContainerHeight * -1;
          }
        }
      });

      this.resizeProductInfoObserver.observe(
        this.productInfoContainer
      );
    }

    onScrollBuyTools() {
      if (this.bpSmall || !this.buyToolsTrigger) return;

      const triggerTop =
        this.buyToolsTrigger.getBoundingClientRect().top;

      const buyToolsModalActive =
        this.productInfoContainer.classList.contains(
          'product__info-container--modal'
        );

      const buyToolsModalOut =
        this.productInfoContainer.classList.contains(
          'product__info-container--modal--out'
        );

      if (
        triggerTop < this.negativeHeight &&
        !buyToolsModalActive
      ) {
        requestAnimationFrame(() => {
          this.productInfoContainer.classList.add(
            'product__info-container--modal',
            'background-modal'
          );
        });
      } else if (
        triggerTop > this.negativeHeight &&
        buyToolsModalActive &&
        !buyToolsModalOut
      ) {
        requestAnimationFrame(() => {
          this.productInfoContainer.classList.add(
            'product__info-container--modal--out'
          );

          this.productInfoContainer.addEventListener(
            'animationend',
            () => {
              this.productInfoContainer.classList.remove(
                'product__info-container--modal--out'
              );

              this.productInfoContainer.classList.remove(
                'product__info-container--modal',
                'background-modal'
              );
            },
            { once: true }
          );
        });
      }
    }

    get productForm() {
      return this.querySelector('product-form');
    }

    get productModal() {
      return document.querySelector(
        `#ProductModal-${this.sectionId}`
      );
    }

    get pickupAvailability() {
      return this.querySelector('pickup-availability');
    }

    get variantSelectors() {
      return this.querySelector('variant-selects');
    }

    get relatedProducts() {
      const relatedProductsSectionId =
        SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );

      return document.querySelector(
        `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`
      );
    }

    get sectionId() {
      return this.dataset.originalSection || this.dataset.section;
    }

    get blockId() {
      return this.dataset.block;
    }

    get productId() {
      return this.dataset.productId;
    }
  });
}
